import {CommonModule} from '@angular/common';

import {HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';

import {NgModule} from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {ActivatedRoute, Router, RouterModule} from '@angular/router';

import {ChartModule} from 'angular-highcharts';
import 'highcharts/modules/stock';
import 'highcharts/modules/exporting';
import 'highcharts/modules/export-data';
import 'highcharts/modules/accessibility';

import {LoggerModule, NGXLogger} from 'ngx-logger';
import {environment} from '../environments/environment';

import {ApiDataService} from './api/api.data.service';
import {mockEncodedJwtResponse} from './api/mock.objects';

import {AppComponent} from './app.component';
import {AppRoutingModule} from './app.routing.module';

import {AuthenticationService} from './authentication/service/authentication.service';
import {AuthGuard} from './components/authorization/service/auth-guard';
import {ResourceRightsService} from './components/authorization/service/resource-rights.service';
import {MainModule} from './components/main.module';

import {Configs} from './config';

import {ErrorComponent} from './error/error.component';
import {NotAuthorizedComponent} from './error/not-authorized-component';
import {PageNotFoundComponent} from './error/page-not-found.component';
import {LoginModule} from './login/login.module';
import {LoginService} from './login/service/login.service';
import {ExternalUrlComponent} from './shared/external-url/external-url.component';
import {ExternalUrlResolver} from './shared/external-url/external-url.resolver';
import {StatusModalService} from './shared/modal/status-modal.service';
import {HttpInterceptorService} from './shared/service/http-interceptor.service';
import {SessionService} from './shared/service/session.service';
import {SharedModule} from './shared/shared.module';

@NgModule({
  exports: [],
  declarations: [
    AppComponent,
    ExternalUrlComponent,
    ErrorComponent,
    PageNotFoundComponent,
    NotAuthorizedComponent,
  ],
  bootstrap: [
    AppComponent,
  ],
  imports: [
    BrowserAnimationsModule,
    BrowserModule,
    ChartModule,
    CommonModule,
    RouterModule,
    AppRoutingModule,
    MainModule,
    LoggerModule.forRoot(Configs.LOGGER_CONFIG),
    LoginModule,
    SharedModule,
  ],
  providers: [
    StatusModalService,
    NGXLogger,
    ExternalUrlResolver,
    ResourceRightsService,
    {
      provide: AuthGuard,
      useFactory: AuthGuardLoader,
      deps: [Router, ActivatedRoute, SessionService, NGXLogger],
    },
    {
      provide: SessionService,
      useFactory: sessionServiceLoader,
      deps: [NGXLogger, ResourceRightsService],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpInterceptorService,
      multi: true,
    },
    {
      provide: ApiDataService,
      useFactory: restServiceLoader,
      deps: [NGXLogger, HttpClient, Router, StatusModalService],
    },
    {
      provide: LoginService,
      useFactory: loginServiceLoader,
      deps: [NGXLogger, AuthenticationService, Router, SessionService],
    },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})

export class AppModule {
  constructor() {
    if (environment.env === 'local') {
      sessionStorage.setItem('access_token', mockEncodedJwtResponse['access_token']);
    }
  }
}

export function AuthGuardLoader(
  router: Router,
  route: ActivatedRoute,
  sessionService: SessionService,
  logger: NGXLogger
) {
  return new AuthGuard(router, route, sessionService, logger);
}

export function restServiceLoader(
  logger: NGXLogger,
  http: HttpClient,
  router: Router,
  statusModalService: StatusModalService,
  loginService: LoginService
): any {
  return new ApiDataService(logger, http, router, statusModalService, loginService);
}

export function loginServiceLoader(
  logger: NGXLogger,
  api: AuthenticationService,
  router: Router,
  sessionService: SessionService
): any {
  return new LoginService(logger, api);
}

export function getCurrentDate(): Date {
  return new Date();
}

export function sessionServiceLoader(
  logger: NGXLogger,
  resourceRightsService: ResourceRightsService
): any {
  return new SessionService(logger, resourceRightsService);
}
